import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import PDV from "@/pages/PDV";
import Products from "@/pages/Products";
import Stock from "@/pages/Stock";
import AccountsPayable from "@/pages/AccountsPayable";
import AccountsReceivable from "@/pages/AccountsReceivable";
import Reports from "@/pages/Reports";
import Users from "@/pages/Users";
import Customers from "@/pages/Customers";
import Suppliers from "@/pages/Suppliers";
import Purchases from "@/pages/Purchases";
import Quotes from "@/pages/Quotes";
import Inventory from "@/pages/Inventory";
import Sales from "@/pages/Sales";
import Settings from "@/pages/Settings";
import Pricing from "@/pages/Pricing";
import ProductManagement from "@/pages/ProductManagement";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center">Carregando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function RouterShell() {
  const location = useLocation();
  // Emergent OAuth returns here with #session_id=... — handle it BEFORE any protected route
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<Protected><Layout /></Protected>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="pdv" element={<PDV />} />
        <Route path="vendas" element={<Sales />} />
        <Route path="orcamentos" element={<Quotes />} />
        <Route path="produtos" element={<Products />} />
        <Route path="gestao-produtos" element={<ProductManagement />} />
        <Route path="precificacao" element={<AdminOnly><Pricing /></AdminOnly>} />
        <Route path="clientes" element={<Customers />} />
        <Route path="fornecedores" element={<Suppliers />} />
        <Route path="compras" element={<Purchases />} />
        <Route path="estoque" element={<Stock />} />
        <Route path="inventario" element={<Inventory />} />
        <Route path="contas-a-pagar" element={<AdminOnly><AccountsPayable /></AdminOnly>} />
        <Route path="contas-a-receber" element={<AdminOnly><AccountsReceivable /></AdminOnly>} />
        <Route path="relatorios" element={<AdminOnly><Reports /></AdminOnly>} />
        <Route path="configuracoes" element={<AdminOnly><Settings /></AdminOnly>} />
        <Route path="usuarios" element={<AdminOnly><Users /></AdminOnly>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <RouterShell />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
